import { GITHUB_PR_URL_SOURCE } from '@reflex/shared';
import type { PluginWebManifest } from '@reflex/plugin-api/web';

export const githubWeb: PluginWebManifest = {
  // Org-tier setup is "install the GitHub App on this org" — that
  // lives in GitHubAppSetupPage. Mounted in the wizard slide-over
  // and the plugin detail page. Note the `adminSetupComponent` below
  // is the SERVICE-tier setup (host operator configures the App's
  // GitHub identity) — a separate surface from the org-tier install.
  orgSetupComponent: {
    componentPath: '@reflex/plugin-github/web/GitHubAppSetupPage',
    label: 'Install the App on the GitHub account that owns your repos.',
  },
  attachments: [
    {
      id: 'git-repo',
      label: 'Git Repository',
      icon: 'GitBranch',
      order: 10,
      pickerComponentPath: '@reflex/plugin-github/web/GitRepoAttachmentPicker',
      summaryComponentPath: '@reflex/plugin-github/web/GitRepoAttachmentSummary',
    },
  ],
  launchMentionProviders: [
    {
      id: 'launch-mention-git-repo',
      label: 'Repositories',
      keyword: 'repo',
      icon: 'GitBranch',
      componentPath: '@reflex/plugin-github/web/GitRepoMentionProvider',
      order: 20,
    },
  ],
  // Searchable "reference this as context" categories, each paired with a
  // server resolver registered in `register()` (github-context-resolvers.ts).
  // Note `agent-context-github-repos` REFERENCES a repo as context — distinct
  // from `launch-mention-git-repo` above, which sets the run's attachment.
  agentContextProviders: [
    {
      id: 'agent-context-github-prs',
      kind: 'github-pr',
      label: 'Pull requests',
      keyword: 'pr',
      icon: 'GitPullRequest',
      componentPath: '@reflex/plugin-github/web/GitHubPrContextProvider',
      // Pasting a PR link into the composer is how people actually carry a
      // pull request over from GitHub, so it produces the same `github-pr`
      // chip (and the same `owner/repo#42` id the resolver expands) that
      // picking one from this menu does.
      // Only the PR itself is described here; the tabs and anchors a link
      // picks up on its way through a browser or a review comment (`/files`,
      // `.diff`, `#discussion_r1`) are consumed by the registry, since they
      // all name the same pull request.
      urlPattern: GITHUB_PR_URL_SOURCE,
      urlFlags: 'i',
      urlIdTemplate: '$1/$2#$3',
      mentionBadgeComponentPath: '@reflex/plugin-github/web/GitHubPrMentionBadge',
      order: 10,
    },
    {
      id: 'agent-context-github-issues',
      kind: 'github-issue',
      label: 'GitHub issues',
      keyword: 'issue',
      icon: 'CircleDot',
      componentPath: '@reflex/plugin-github/web/GitHubIssueContextProvider',
      order: 20,
    },
    {
      id: 'agent-context-github-repos',
      kind: 'github-repo',
      // Not "Repositories": the attach category above already owns that
      // label, and two identical rows in the menu invite picking the wrong
      // action (attach vs reference).
      label: 'Repository details',
      keyword: 'repository',
      icon: 'BookMarked',
      componentPath: '@reflex/plugin-github/web/GitHubRepoContextProvider',
      order: 30,
    },
    {
      id: 'agent-context-github-branches',
      kind: 'github-branch',
      label: 'Branches',
      keyword: 'branch',
      icon: 'GitBranch',
      componentPath: '@reflex/plugin-github/web/GitHubBranchContextProvider',
      order: 40,
    },
  ],
  // A pull-request link is the one URL in agent output that carries a state
  // worth reading at a glance, so it renders as the PR chip (icon, number,
  // hover card) instead of a bare link. Ordered ahead of the link unfurler's
  // catch-all (order 100) so the PR badge wins.
  contentScanners: [
    {
      id: 'github:pr-url',
      target: 'url',
      pattern: `^${GITHUB_PR_URL_SOURCE}`,
      flags: 'i',
      componentPath: '@reflex/plugin-github/web/GitHubPrLinkBadge',
      order: 10,
    },
  ],
  adminSetupComponent: {
    componentPath: '@reflex/plugin-github/web/GitHubAppAdminSetupPanel',
    label:
      "Register the GitHub App on github.com, paste the Setup URL below into the app's Post installation field, and bootstrap the app's credentials through environment variables.",
  },
};
